import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
} from '@mui/material';
import numeral from 'numeral';
import { SectionBase } from './SectionBase';

const getPercentage = (value, total) => {
  return value ? Math.round((value * 100) / total) : '';
};

const optionsToPercentage = (value, total) => {
  const result = {};
  value?.forEach(v => {
    result[v.id] = getPercentage(v.value, total);
  });
  return result;
};

export const SectionProgramYearAmount = ({
  title,
  subTitle,
  helpText,
  error,
  questionId,
  value,
  onChange,
  options,
  total,
  onSetError,
  guidanceText,
}) => {
  const [percentage, setPercentage] = useState(
    optionsToPercentage(value, total),
  );
  const handleChangeValue = useCallback(
    (event, id) => {
      const newValue = (value || [])?.map(row => ({ ...row }));
      const found = newValue?.find(v => v.id === id);
      const number = makeNumber(event.target.value);
      if (found) {
        found.value = number;
      } else {
        newValue.push({ id, value: number });
      }
      setPercentage(p => ({ ...p, [id]: getPercentage(number, total) }));
      onChange(questionId, newValue);
    },
    [value, questionId, onChange],
  );
  const handleChangePercent = useCallback(
    (event, id) => {
      const newPercentage = parseInt(event.target.value || 0);
      if (newPercentage < 0 || newPercentage > 100) return;
      setPercentage(p => ({ ...p, [id]: newPercentage }));
      const newValue = (value || [])?.map(row => ({ ...row }));
      const found = newValue?.find(v => v.id === id);
      const calc = (Math.round(newPercentage) * total) / 100;
      if (found) {
        found.value = calc;
      } else {
        newValue.push({ id, value: calc });
      }
      onChange(questionId, newValue);
    },
    [value, total, onChange, questionId],
  );
  useEffect(() => {
    const sum = value?.reduce((all, { value }) => all + value * 1, 0);
    if (sum === total || !sum) {
      onSetError();
    }
  }, [value, total, onSetError]);

  return (
    <SectionBase
      title={title}
      subTitle={subTitle}
      helpText={helpText}
      questionId={questionId}
      guidanceText={guidanceText}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Program</TableCell>
              <TableCell>Share of Donation</TableCell>
              <TableCell>Percent of Donation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {options?.map(({ id, name, year }) => {
              const row = value?.find(v => v.id === id);
              return (
                <TableRow key={id}>
                  <TableCell>
                    <div>
                      <div>
                        <b>{name}</b>
                      </div>
                      <div>{year}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Label>
                      <span>$</span>
                      <TextField
                        value={row?.value && numeral(row?.value).format(0, 0)}
                        onChange={event => handleChangeValue(event, id)}
                      />
                    </Label>
                  </TableCell>
                  <TableCell>
                    <Label>
                      <TextField
                        disabled={!total}
                        type="number"
                        min="0"
                        max="100"
                        value={percentage[id] || ''}
                        onChange={event => handleChangePercent(event, id)}
                      />
                      <span>%</span>
                    </Label>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </SectionBase>
  );
};

const Label = styled.label`
  display: flex;
  align-items: center;

  & > span:first-child {
    margin-right: 10px;
  }
  & > span:last-child {
    margin-left: 10px;
  }
`;

const makeNumber = n => n && (parseInt(n) || 0);
